import { ApplicationConfig, provideBrowserGlobalErrorListeners, isDevMode, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { DialogModule } from '@angular/cdk/dialog';

import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideState, provideStore } from '@ngrx/store';
import { provideTransloco } from '@jsverse/transloco';
import { provideEffects } from '@ngrx/effects';

import { InspectionsEffects } from '@features/inspections/pages/super-admin/state/inspections.effects';
import { inspectionsReducer } from '@features/inspections/pages/super-admin/state/inspections.reducer';
import { loaderInterceptor } from '@core/utils/loader.interceptor';
import { TranslocoHttpLoader } from './transloco-loader';
import { importProvidersFrom } from '@angular/core';
import { authInterceptor } from '@core/auth';
import { environment } from '@env';

import { routes } from './app.routes';

function loadGoogleMapsApi(apiKey: string): () => Promise<void> {
  return () =>
    new Promise<void>((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geocoding,places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps API'));
      document.head.appendChild(script);
    });
}

const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => loadGoogleMapsApi(environment.googleMapsApiKey)()),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    provideHttpClient(withInterceptors([loaderInterceptor, authInterceptor])),
    importProvidersFrom(DialogModule),
    provideStore(),
    provideEffects([InspectionsEffects]),
    provideState('common-pages.inspections',
      inspectionsReducer,
    ),
    provideStoreDevtools({ maxAge: 25, logOnly: false }),
    provideTransloco({
      config: {
        availableLangs: ['en', 'es'],
        defaultLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    })
  ]
};

export { appConfig };
